// The branded confirm for sending Enquiry replies (Session ATW, email system scope defect 15). One component for the one
// reply on an enquiry's page and for the bulk send on the Enquiries page, so the two cannot name recipients differently.
//
// WHO IT NAMES. A reply now goes to whoever wrote (their Reply-To, else their From), in their own conversation, never to
// the matched customer's billing address. That is decided on the server from the conversation as Gmail holds it, so the
// confirm asks the server (GET /api/enquiries/:id/send-preview, never cached) and names exactly what it says. The
// recipient it named is handed back with the send (expectedTo), and the server refuses a send that would now reach
// someone else (a newer message in the conversation), so the person is never sending to someone they were not shown.
//
// Send stays disabled until every reply's recipient has been read, and while any of them cannot be sent.
//
// Session AUB: the Mail workspace's Approve and send opens this same confirm. Every reply carries the ONE Company signature
// (DE-3), so the copy no longer speaks of the office's Gmail signature.
//
// Session ATZ (Item 7): it also names the address each reply goes FROM (the ELE address the customer wrote to, when Gmail
// lets the account send from it) and why, exactly as the server's preview says.
import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import type { EnquirySendPreview } from "../../lib/api";
import { friendlyApiError } from "../../lib/friendlyApiError";
import { ModalShell, MODAL_ACTIONS, MODAL_PRIMARY_BTN, MODAL_SECONDARY_BTN } from "../../components/ModalShell";

export interface EnquirySendTarget {
  id: string;
  label: string; // the subject or sender, for naming the row
}

type Loaded = { kind: "loading" } | { kind: "ready"; preview: EnquirySendPreview } | { kind: "error"; message: string };

function copiedCount(cc: string | null): number {
  return cc ? cc.split(",").filter((a) => a.trim()).length : 0;
}

export function EnquirySendConfirmModal({
  targets,
  busy,
  error,
  onConfirm,
  onCancel,
}: {
  targets: EnquirySendTarget[];
  busy: boolean;
  error: string | null;
  /** Called with each enquiry's confirmed recipient, to send as expectedTo. */
  onConfirm: (recipients: Map<string, string>) => void;
  onCancel: () => void;
}) {
  const [loaded, setLoaded] = useState<Map<string, Loaded>>(() => new Map(targets.map((t) => [t.id, { kind: "loading" } as Loaded])));
  const idsKey = targets.map((t) => t.id).join("|");

  useEffect(() => {
    let live = true;
    setLoaded(new Map(targets.map((t) => [t.id, { kind: "loading" } as Loaded])));
    for (const t of targets) {
      api.enquiries
        .sendPreview(t.id)
        .then((res) => live && setLoaded((m) => new Map(m).set(t.id, { kind: "ready", preview: res.preview })))
        .catch((err) => live && setLoaded((m) => new Map(m).set(t.id, { kind: "error", message: friendlyApiError(err, "Could not read this conversation.") })));
    }
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey]);

  const entries = targets.map((t) => ({ target: t, state: loaded.get(t.id) ?? ({ kind: "loading" } as Loaded) }));
  const stillLoading = entries.some((e) => e.state.kind === "loading");
  const blocked = entries.filter((e) => e.state.kind === "error" || (e.state.kind === "ready" && (!e.state.preview.sendable || !e.state.preview.to)));
  const canSend = !busy && !stillLoading && blocked.length === 0 && entries.length > 0;
  const count = targets.length;

  function confirm() {
    const recipients = new Map<string, string>();
    for (const e of entries) if (e.state.kind === "ready" && e.state.preview.to) recipients.set(e.target.id, e.state.preview.to);
    onConfirm(recipients);
  }

  return (
    <ModalShell ariaLabel={count === 1 ? "Send this reply?" : "Send the drafted replies?"} onClose={() => !busy && onCancel()} closeOnBackdrop width="max-w-lg" panelClassName="p-5 shadow-xl">
      <h2 className="font-heading text-lg text-ele-strong">{count === 1 ? "Send this reply?" : `Send ${count} drafted replies?`}</h2>
      <p className="mt-2 text-sm text-ele-body">
        Each reply goes to the person who wrote, in their own email conversation, signed off with the Company signature (never a
        person&apos;s own). It cannot be recalled once sent.
      </p>
      <ul className="mt-3 max-h-72 space-y-2 overflow-y-auto" aria-label="Who each reply goes to">
        {entries.map(({ target, state }) => (
          <li key={target.id} className="rounded-ele border border-ele-hairline bg-ele-inset px-3 py-2 text-sm">
            <p className="truncate text-ele-strong">{target.label}</p>
            {state.kind === "loading" && <p className="text-xs text-ele-body">Reading the conversation...</p>}
            {state.kind === "error" && (
              <p role="alert" className="text-xs text-ele-danger">
                {state.message}
              </p>
            )}
            {state.kind === "ready" &&
              (state.preview.sendable && state.preview.to ? (
                <>
                  <p className="text-xs text-ele-body [overflow-wrap:anywhere]">
                    To <span className="font-semibold text-ele-strong">{state.preview.to}</span>
                    {copiedCount(state.preview.cc) > 0 &&
                      ` (and ${copiedCount(state.preview.cc)} other${copiedCount(state.preview.cc) === 1 ? "" : "s"} they copied: ${state.preview.cc})`}
                    {state.preview.threaded ? ", as a reply in the conversation." : ". This one cannot be placed in the conversation, so it goes as a separate email."}
                  </p>
                  {/* Session ATZ (Item 7): which of our addresses it goes from, and why. */}
                  {/* Session AUB: the From line is always shown (Item 8: the confirm names every recipient AND the From
                      address), with the server's reason when it gave one. */}
                  <p className="mt-1 text-xs text-ele-body [overflow-wrap:anywhere]" data-testid="send-from">
                    From <span className="font-semibold text-ele-strong">{state.preview.from ?? "the account's default address"}</span>.
                    {state.preview.fromReason ? ` ${state.preview.fromReason}` : ""}
                  </p>
                  {/* Session AUA (DE-3): every reply carries the one company signature, never a person's own. */}
                  {state.preview.signature && (
                    <p className="mt-1 text-xs text-ele-body" data-testid="send-signature">
                      Signature: <span className="font-semibold text-ele-strong">{state.preview.signature}</span>
                    </p>
                  )}
                </>
              ) : (
                <p role="alert" className="text-xs text-ele-danger">
                  {state.preview.refusal ?? "This reply cannot be sent."}
                </p>
              ))}
          </li>
        ))}
      </ul>
      {blocked.length > 0 && !stillLoading && (
        <p className="mt-3 text-sm text-ele-body">
          {count === 1 ? "This reply cannot be sent as it stands." : "Deselect the replies that cannot be sent, then try again."}
        </p>
      )}
      {error && (
        <p role="alert" className="mt-3 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">
          {error}
        </p>
      )}
      <div className={`mt-5 gap-3 ${MODAL_ACTIONS}`}>
        <button type="button" onClick={onCancel} disabled={busy} className={MODAL_SECONDARY_BTN}>
          Cancel
        </button>
        <button type="button" onClick={confirm} disabled={!canSend} className={MODAL_PRIMARY_BTN}>
          {busy ? "Sending..." : stillLoading ? "Checking..." : "Send"}
        </button>
      </div>
    </ModalShell>
  );
}
